import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ImageOff, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { ProjectCard, ProjectModal } from "../components/ProjectGallery";
import { api } from "../lib/api";

export default function Gallery() {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  return (
    <div data-testid="gallery-page">
      <section className="bg-charcoal text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Eyebrow>Our Work</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-700 mt-5 max-w-3xl leading-[1.08] tracking-tight text-balance">
              Real projects, from first cut to final reveal.
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="text-white/75 text-lg mt-6 max-w-2xl leading-relaxed">
              Click any project to walk through the full timeline — before, during, and after.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {items === null ? (
            <div className="flex items-center gap-2 text-olive py-20 justify-center" data-testid="gallery-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading our work…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 bg-[#F8F9FA] rounded-sm border border-border" data-testid="gallery-empty">
              <ImageOff className="w-12 h-12 text-olive/40 mx-auto mb-4" />
              <h2 className="font-display text-2xl font-600 text-charcoal mb-2">Project gallery coming soon</h2>
              <p className="text-olive max-w-md mx-auto">We're putting together photos of our recent projects. Check back shortly!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
              {items.map((p, idx) => (
                <Reveal key={p.id} delay={idx % 3}><ProjectCard project={p} onOpen={setActive} /></Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-emerald text-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal><h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-600 leading-tight text-balance">Want results like these on your property?</h2></Reveal>
          <Reveal delay={1}><p className="text-white/80 text-lg mt-5 max-w-2xl mx-auto">Send us a few photos and we'll put together a fair, no-pressure quote.</p></Reveal>
          <Reveal delay={2}>
            <Link to="/quote" className="inline-flex items-center gap-2 bg-gold text-charcoal px-9 py-4 rounded-sm font-700 mt-9 hover:bg-gold-dark transition-colors" data-testid="gallery-quote-btn">
              Request a Free Quote <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {active && <ProjectModal project={active} onClose={() => setActive(null)} />}
    </div>
  );
}
